import { PocButton, PocComponentHeader, PocIcon, PocTypography } from 'commons/components/components-wrapper';
import { OwcCheckboxCustomEvent, Ref } from '@one/web-components/dist/types/components';
import { useTranslation } from 'core/i18n/i18n';
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveHeaderGroups } from './custom-table-header-groups.business';
import { CustomTableLeafHeaderComponent } from './custom-table-leaf-header.component';
import { CustomTableConfig, CustomTableHeaderGroup, FieldConfig, HeaderOptions } from './custom-table.type';
import {
    CustomTableGroupHeaderCell,
    CustomTableGroupHeaderGrid,
    CustomTableGroupHeaderViewport,
    CustomTableHeaderRows,
    TableHeaderLayout,
    TableHeaderOptionsLayout,
    TableHeaderTitleLayout,
} from './custom-table.style';
import { TableFiltersComponent, TableFiltersComponentProps, TableFiltersRef } from './filters/table-filters.component';
import { useStickyHeader } from './hooks/use-sticky-header.hook';
import { useCustomTableSorting } from './sort/use-custom-table-sorting.hook';
import { CustomTableSortingProps } from './sort/custom-table-sorting.model';

const allRowsAreSelected = <T extends { id: string }>(tableData: T[], selectedIds: Set<string>): boolean =>
    Boolean(tableData.length && tableData.every((d) => selectedIds.has(d.id)));

const someRowsAreSelected = <T extends { id: string }>(tableData: T[], selectedIds: Set<string>): boolean =>
    tableData?.some((d) => selectedIds.has(d.id)) ?? false;

const getDefaultHeaderCheckboxProps = <T extends { id: string }>(
    tableData: T[],
    selectedIds: Set<string>,
    setSelectedRowIds?: React.Dispatch<React.SetStateAction<Set<string>>>
) => ({
    checked: allRowsAreSelected(tableData, selectedIds),
    indeterminate: !allRowsAreSelected(tableData, selectedIds) && someRowsAreSelected(tableData, selectedIds),
    onValueChange: (ev: OwcCheckboxCustomEvent<boolean>) => {
        ev.preventDefault();
        setSelectedRowIds?.((prevSelectedRowIds) => {
            const next = new Set(prevSelectedRowIds);

            const someSelected = tableData.some((d) => next.has(d.id));
            if (someSelected) {
                // deselect visible rows
                for (const d of tableData) {
                    next.delete(d.id);
                }
            } else {
                // select visible rows
                for (const d of tableData) {
                    next.add(d.id);
                }
            }

            return next;
        });
    },
});

interface CustomTableHeaderComponentProps<T extends { id: string }> {
    headerTitle: string;
    headerTitleExtraInfo?: string;
    totalElements: number;
    showTotalElements: boolean;
    config: CustomTableConfig<T>;
    loading: boolean;
    tableData: T[];
    emptyMessage: string | React.ReactElement;
    filterProps?: TableFiltersComponentProps;
    sortingProps?: CustomTableSortingProps;
    selectedRowIds?: Set<string>;
    editColumns: boolean;
    optionComponents?: React.ReactNode;
    fieldsToShow: FieldConfig<T>[];
    defaultHeaderOptions: HeaderOptions;
    showComponentHeader: boolean;
    headerGroups?: CustomTableHeaderGroup[];
    headerRef: RefObject<HTMLOwcTableHeaderElement>;
    groupHeaderRef: RefObject<HTMLDivElement>;
    scrollContainerRef?: RefObject<HTMLDivElement>;
    headerCheckboxProps?: Partial<HTMLOwcCheckboxElement & Ref<HTMLOwcCheckboxElement>>;
    onEditColumns?: VoidFunction;
    setSelectedRowIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const CustomTableHeaderComponent = <T extends { id: string }>(props: CustomTableHeaderComponentProps<T>) => {
    const {
        headerTitle,
        headerTitleExtraInfo,
        tableData,
        selectedRowIds = new Set<string>(),
        totalElements,
        showTotalElements,
        editColumns,
        optionComponents = null,
        filterProps,
        sortingProps,
        fieldsToShow = [],
        config,
        defaultHeaderOptions,
        showComponentHeader,
        headerGroups,
        headerRef,
        groupHeaderRef,
        scrollContainerRef,
        headerCheckboxProps,
        onEditColumns,
        setSelectedRowIds,
    } = props;

    const { t } = useTranslation();
    const location = useLocation();
    const filterHeaderRef = useRef<TableFiltersRef>(null);
    const [showAllFilters, setShowAllFilters] = useState(false);

    const { headerSortOrder, handleSortOrderChange } = useCustomTableSorting<T>({
        fieldsToShow,
        sortingProps,
    });

    const { isVisibleFilter } = useStickyHeader({
        headerRef,
        scrollContainerRef,
        filterHeaderRef,
        freezeVisibility: showAllFilters,
    });

    const defaultCheckboxProps = useMemo(
        () => getDefaultHeaderCheckboxProps(tableData, selectedRowIds, setSelectedRowIds),
        [tableData, selectedRowIds, setSelectedRowIds]
    );

    const preventDefaultHandler = useCallback((ev: Event) => ev.preventDefault(), []);

    const handleCellClick = useCallback(
        (fieldName: string, sortable: boolean) => (ev: React.MouseEvent) => {
            if (!sortable) {
                ev.preventDefault();
                return;
            }
            handleSortOrderChange(fieldName);
        },
        [handleSortOrderChange]
    );

    const validFields = useMemo(() => fieldsToShow.filter(Boolean), [fieldsToShow]);

    const resolvedHeaderGroups = useMemo(
        () => (headerGroups?.length ? resolveHeaderGroups(headerGroups, config.fields, validFields) : []),
        [headerGroups, config.fields, validFields]
    );

    const groupHeaderGridTemplateColumns = useMemo(
        () => validFields.map((field) => field.headerOptions.width ?? defaultHeaderOptions.width).join(' '),
        [defaultHeaderOptions.width, validFields]
    );

    const leafHeaderProps = {
        entity: config.entity,
        fields: validFields,
        className: filterProps?.children && isVisibleFilter ? '' : 'no-filters',
        headerRef,
        checkboxProps: headerCheckboxProps ?? defaultCheckboxProps,
        defaultHeaderOptions,
        headerSortOrder,
        onSortOrderChange: preventDefaultHandler,
        onCellClick: handleCellClick,
    };

    return (
        <>
            {showComponentHeader ? (
                <PocComponentHeader slot="header">
                    <TableHeaderLayout>
                        <TableHeaderTitleLayout>
                            <PocTypography element="span" variant="button">
                                {headerTitle}
                            </PocTypography>
                            {showTotalElements ? (
                                <PocTypography element="span" variant="subtitle1" data-test-id="total-elements">
                                    ({totalElements || '-'})
                                </PocTypography>
                            ) : undefined}
                            {headerTitleExtraInfo ? (
                                <PocTypography className="header-extra-info" element="span" variant="caption">
                                    {headerTitleExtraInfo}
                                </PocTypography>
                            ) : undefined}
                        </TableHeaderTitleLayout>
                        <TableHeaderOptionsLayout>
                            {editColumns ? (
                                <PocButton
                                    className="custom-table-edit-button"
                                    variant="tertiary"
                                    onClick={onEditColumns}
                                >
                                    {t('commons.components.table.table-header.options.edit-columns')}
                                    <PocIcon name="columns" family="outlined" slot="prefix" />
                                </PocButton>
                            ) : undefined}
                            {optionComponents}
                        </TableHeaderOptionsLayout>
                    </TableHeaderLayout>
                    {filterProps?.children ? (
                        <TableFiltersComponent
                            {...filterProps}
                            ref={filterHeaderRef}
                            isVisible={isVisibleFilter}
                            onShowAllChange={setShowAllFilters}
                            key={location.key}
                        />
                    ) : undefined}
                </PocComponentHeader>
            ) : undefined}
            {resolvedHeaderGroups.length > 0 ? (
                <CustomTableHeaderRows>
                    {/* The group tier lives outside the ODSU table structure, so it cannot be a real
                        column-header row; expose it as decorative (aria-hidden) and let the leaf
                        PocTableHeaderCell descendants remain the announced column headers. */}
                    <CustomTableGroupHeaderViewport
                        ref={groupHeaderRef}
                        aria-hidden="true"
                        data-testid="custom-table-group-header"
                    >
                        <CustomTableGroupHeaderGrid
                            $gridTemplateColumns={groupHeaderGridTemplateColumns}
                            data-testid="custom-table-group-header-grid"
                        >
                            {resolvedHeaderGroups.map((group) => (
                                <CustomTableGroupHeaderCell
                                    key={group.id}
                                    style={{ gridColumn: `${group.start} / span ${group.span}` }}
                                >
                                    {group.text}
                                </CustomTableGroupHeaderCell>
                            ))}
                        </CustomTableGroupHeaderGrid>
                    </CustomTableGroupHeaderViewport>
                    <CustomTableLeafHeaderComponent {...leafHeaderProps} sticky={false} />
                </CustomTableHeaderRows>
            ) : (
                <CustomTableLeafHeaderComponent {...leafHeaderProps} sticky />
            )}
        </>
    );
};
